import type { Product, ProductImage } from 'product/types/Product'
import type { Stock, StockAvailability } from 'product/types/Stock'
import { env } from 'utils/envManager/envManager'
import type { ItemDto, ProductInfo } from '../services/graphql.types'
import {
	type CartProduct,
	TYPE_IMAGES_DEFAULT,
	TYPE_IMAGES_DEFAULT_TEST,
} from '../types/CartProduct'
import { cartImagesCache } from './cache/cartImagesCache'

export const mapItemDtoToCartProduct = (item: ItemDto): CartProduct => ({
	productId: item.productId,
	colorId: item.colorId,
	sizeId: item.sizeId,
	look: item.look ?? '00',
	customized: item.customized,
	quantity: item.quantity,
	price: item.price,
	outOfStock: item.outOfStock ?? false,
	hasLowStock: item.hasLowStock,
	warehouses: item.warehouses?.filter((w): w is string => w !== null) ?? [],
	itemId: item.itemId,
	seller: item.seller,
})

const pickFirstImage = (
	images?: Record<string, ProductImage>
): ProductImage => {
	if (!images) {
		return { img: '', alt: '' }
	}

	for (const type of TYPE_IMAGES_DEFAULT) {
		const candidate = images[type]
		if (candidate?.img) {
			return candidate
		}
	}

	return { img: '', alt: '' }
}

export function getProductImageTest(
	productInfo: Product | undefined,
	cartProduct: CartProduct | null | undefined
): ProductImage {
	if (!productInfo || !cartProduct) {
		return { img: '', alt: '' }
	}

	const color = productInfo.colors.find((c) => c.id === cartProduct.colorId)
	if (!color) {
		return { img: '', alt: '' }
	}

	const images = color.looks?.[cartProduct.look]?.images
	if (!images) {
		return { img: '', alt: '' }
	}

	for (const type of TYPE_IMAGES_DEFAULT_TEST) {
		const candidate = images[type]
		if (candidate?.img) {
			return { img: candidate.img, alt: candidate.alt }
		}
	}

	return { img: '', alt: '' }
}

export const mapProductInfoToCartProduct = (
	cartProduct: CartProduct,
	product: Product
): CartProduct => {
	const color = product.colors.find((c) => c.id === cartProduct.colorId)

	const images = color?.looks?.[cartProduct.look]?.images
	const imageToShow = pickFirstImage(images)

	const size = color?.sizes.find((s) => s.id === cartProduct.sizeId)

	const info: ProductInfo = {
		colorId: cartProduct.colorId,
		colorName: color?.label ?? '',
		image: imageToShow.img,
		imageAlt: imageToShow.alt,
		name: product.name,
		sizeId: cartProduct.sizeId,
		sizeName: size?.label ?? '',
		genderId: product.genderId,
	}

	return { ...cartProduct, info }
}

export const mapStockToCartProduct = (
	cartProduct: CartProduct,
	stock: Stock
): CartProduct => {
	const stockAvailability: StockAvailability | undefined =
		stock.colors[cartProduct.colorId]?.sizes[cartProduct.sizeId]

	return {
		...cartProduct,
		stock: stockAvailability,
	}
}

export const addCacheCartProductImage = async (
	cartProduct: CartProduct
): Promise<void> => {
	if (!cartProduct.info?.image) {
		return
	}

	try {
		const cacheInstance = cartImagesCache()

		await cacheInstance.addKeyToCache<string>(
			cartProduct.productId,
			`${env.NEXT_PUBLIC_SITE_BASE_URL}${cartProduct.info.image}`
		)
	} catch {
		console.error()
	}
}

export const buildCartProduct = (
	item: ItemDto,
	product: Product,
	stock: Stock
): Readonly<CartProduct> => {
	const productMapped = mapItemDtoToCartProduct(item)
	const productWithInfo = mapProductInfoToCartProduct(productMapped, product)
	const cartProduct = mapStockToCartProduct(productWithInfo, stock)

	void addCacheCartProductImage(cartProduct)

	return cartProduct
}
